/* Copyright (c) 2020-present CloudIO Inc */

import { IoDatasource, Query } from '@cloudio-saas/datasource-types';
import { emptyFunction, useStateXSelector } from '@cloudio-saas/statex';
import { FormControl, FormHelperText } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DataSourceChooserProp } from 'componentregistry';
import { DataStoreItem } from 'componentregistry/components/DataStore/DataStore';
import { VirtualizedSelectInput } from 'custom/SelectInput';
import { itemAtom } from 'designer/atoms';
import { helpTextSelector } from 'designer/state';
import { memo, useEffect, useState } from 'react';
import useAppStore from 'store/useAppStore';
import { getDataSourceListStoreProps } from 'store/utils/utils';
import WaveDots from 'utils/WaveDots';
import { getDefaultValues, isRequired } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles({
  root: {
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
});

export const dsFilter: (appUid?: string) => Query<IoDatasource> = (appUid) => ({
  filter: [],
  data: appUid ? { appUid } : undefined,
  limit: 5000,
  offset: 0,
  projection: {
    datasourceUid: 1,
  },
});

export function DataSourceChooser({
  appUid,
  helpText,
  label,
  onChange,
  required,
  testid,
  value,
  includeCloudIODataSources = true,
  isViewOnlyUser = false,
}: {
  appUid: string;
  helpText?: string;
  label: string;
  onChange: (val: string) => void;
  required?: boolean;
  testid: string;
  value: string;
  includeCloudIODataSources?: boolean;
  isViewOnlyUser?: boolean;
}) {
  const { query, records, isBusy } = useAppStore<IoDatasource>(
    getDataSourceListStoreProps(appUid),
  );
  useEffect(() => {
    query({ props: dsFilter(includeCloudIODataSources ? undefined : appUid) });
  }, [appUid, includeCloudIODataSources, query]);
  const classes = useStyles();

  const options = Object.values(records).map((row) => ({
    value: row.datasourceUid ?? 'Missing datasourceUid!',
  }));

  return (
    <FormControl
      data-testid={testid}
      required={required}
      fullWidth
      variant="standard"
      component="fieldset"
      className={classes.root}
    >
      <VirtualizedSelectInput
        value={value}
        options={options}
        onChange={onChange}
        label={label}
        startIcon={isBusy ? <WaveDots /> : null}
        disabled={isBusy || isViewOnlyUser}
        placeholder={isBusy ? 'Loading ...' : 'Choose a datasource'}
        variant="standard"
        required={required}
      />
      <FormHelperText>{helpText}</FormHelperText>
    </FormControl>
  );
}

function DataSourceChooserProperty({
  appUid,
  activeItemId,
  def,
  pageId,
  property,
  mgr,
  value = getDefaultValues<string>(mgr, property),
  onChange,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<DataSourceChooserProp, string>) {
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const handleChange = (ds: string) => {
    if (ds !== value) {
      onChange(property, ds);
    }
  };

  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });

  const item = platform.getter(itemAtom, {
    params: { appUid, pageId, itemId: activeItemId },
  }) as DataStoreItem;
  const dsAppUid = item.props.dsAppUid ?? appUid;
  const [, setState] = useState([]);

  useEffect(() => {
    def.refreshCallback(() => {
      setState([]);
    });
    return () => {
      def.refreshCallback(emptyFunction);
    };
  }, [activeItemId, appUid, def, pageId, platform]);

  return (
    <DataSourceChooser
      isViewOnlyUser={isViewOnlyUser}
      appUid={dsAppUid}
      testid={property}
      value={value}
      onChange={handleChange}
      label={def.label}
      required={required}
      helpText={helpText}
    />
  );
}

export default memo(DataSourceChooserProperty);
